import type React from 'react';
import { Table } from 'antd';
import { EditOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { useCallback, useMemo, useState } from 'react';
import ChevronDown from 'assets/svg/ChevronDown';
import EditPencilOutlined from 'assets/svg/EditPencilOutlined';
import EditableMetricCell from './EditableMetricCell';
import type { MetricColumnConfig } from '../../../config/tpoGenConfig';
import type { PPGRow } from '../../../types/promoMetrics';

type TableRow = {
    key: string;
    product: string;
    children?: TableRow[];
    userEditedKeys: Set<string>;
    lastCascadedKeys: Set<string>;
    baselineValues: Record<string, number> | undefined;
    [metricKey: string]: unknown;
};

const formatValue = (value: number | undefined, config: MetricColumnConfig): string => {
    if (value === undefined || value === null) return '-';
    const num = config.allowedDecimals > 0 ? value.toFixed(config.allowedDecimals) : value.toLocaleString();
    return `${config.preUnit}${num}${config.postUnit}`;
};

type CellHighlight = 'user-edited' | 'cascaded' | 'changed-from-baseline' | 'none';

function getCellHighlight(metricKey: string, currentValue: number | undefined, record: TableRow): CellHighlight {
    if (record.userEditedKeys.has(metricKey)) return 'user-edited';
    if (record.lastCascadedKeys.has(metricKey)) return 'cascaded';

    const baselineVal = record.baselineValues?.[metricKey];
    if (baselineVal !== undefined && currentValue !== undefined && baselineVal !== currentValue) {
        return 'changed-from-baseline';
    }

    return 'none';
}

const HIGHLIGHT_STYLES: Record<CellHighlight, React.CSSProperties> = {
    'user-edited': { backgroundColor: '#DBEAFE', borderLeft: '3px solid #60A5FA' },
    cascaded: { backgroundColor: '#FEF3C7', borderLeft: '3px solid #FBBF24' },
    'changed-from-baseline': { backgroundColor: '#D1FAE5', borderLeft: '3px solid #34D399' },
    none: {},
};

const toTableRow = (
    ppg: PPGRow,
    configs: MetricColumnConfig[],
    baseline: Map<string, Record<string, number>>,
    children?: TableRow[],
): TableRow => {
    const row: TableRow = {
        key: String(ppg.ppg_id),
        product: ppg.ppg_name,
        children,
        userEditedKeys: ppg.userEditedKeys,
        lastCascadedKeys: ppg.lastCascadedKeys,
        baselineValues: baseline.get(String(ppg.ppg_id)),
    };
    for (const config of configs) {
        row[config.key] = ppg.values[config.key];
    }
    return row;
};

const buildTreeData = (
    ppgs: PPGRow[],
    configs: MetricColumnConfig[],
    baseline: Map<string, Record<string, number>>,
): TableRow[] =>
    ppgs.map((ppg) => {
        const childRows = ppg.children?.map((child) => toTableRow(child, configs, baseline));
        return toTableRow(ppg, configs, baseline, childRows);
    });

const buildFlatData = (
    ppgs: PPGRow[],
    configs: MetricColumnConfig[],
    baseline: Map<string, Record<string, number>>,
): TableRow[] => ppgs.map((ppg) => toTableRow(ppg, configs, baseline));

const buildColumns = (
    configs: MetricColumnConfig[],
    isTreeView: boolean,
    expandedRowKeys: string[],
    onToggleExpand: (key: string) => void,
    onMetricChange: (ppgId: string, metricKey: string, newValue: number) => void,
    showEditableIndicators: boolean,
): ColumnsType<TableRow> => [
    {
        title: 'Products',
        dataIndex: 'product',
        key: 'product',
        width: 270,
        fixed: 'left',
        render: (text: string, record: TableRow) => {
            const hasChildren = isTreeView && record.children && record.children.length > 0;
            const isExpanded = expandedRowKeys.includes(record.key);
            const isChild = isTreeView && !record.children;

            return (
                <div className="flex items-center gap-2">
                    <span
                        className={`text-sm text-[#18181B] ${hasChildren ? 'font-medium' : 'font-normal'} ${
                            isChild ? 'pl-5' : ''
                        }`}
                    >
                        {text}
                    </span>
                    {hasChildren ? (
                        <span className="cursor-pointer flex-shrink-0" onClick={() => onToggleExpand(record.key)}>
                            <ChevronDown className={isExpanded ? 'rotate-180' : 'rotate-0'} size={12} />
                        </span>
                    ) : null}
                </div>
            );
        },
    },
    ...configs.map((config) => ({
        title:
            showEditableIndicators && config.editable ? (
                <div className="flex items-center gap-1">
                    <span>{config.title}</span>
                    <EditOutlined />
                </div>
            ) : (
                config.title
            ),
        dataIndex: config.key,
        key: config.key,
        width: config.editable ? 140 : 120,
        onCell: (record: TableRow) => {
            const value = record[config.key] as number | undefined;
            const highlight = getCellHighlight(config.key, value, record);
            return {
                style: {
                    ...HIGHLIGHT_STYLES[highlight],
                    transition: 'background-color 0.3s, border-left 0.3s',
                },
            };
        },
        render: (value: number | undefined, record: TableRow) => {
            if (config.editable) {
                return (
                    <EditableMetricCell
                        value={value ?? 0}
                        config={config}
                        onCommit={(newValue) => onMetricChange(record.key, config.key, newValue)}
                        showEditableIndicator={showEditableIndicators}
                    />
                );
            }
            return <span className="text-sm text-[#18181B]">{formatValue(value, config)}</span>;
        },
    })),
];

type PromoMetricsTableProps = {
    metricConfigs: MetricColumnConfig[];
    isTreeView: boolean;
    ppgData: PPGRow[];
    onMetricChange: (ppgId: string, metricKey: string, newValue: number) => void;
    baseline: Map<string, Record<string, number>>;
    showEditableIndicators?: boolean;
};

const PromoMetricsTable = ({
    metricConfigs,
    isTreeView,
    ppgData,
    onMetricChange,
    baseline,
    showEditableIndicators = false,
}: PromoMetricsTableProps) => {
    const [expandedRowKeys, setExpandedRowKeys] = useState<string[]>([]);

    const onToggleExpand = useCallback((key: string) => {
        setExpandedRowKeys((prev) => (prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]));
    }, []);

    const columns = useMemo(
        () =>
            buildColumns(
                metricConfigs,
                isTreeView,
                expandedRowKeys,
                onToggleExpand,
                onMetricChange,
                showEditableIndicators,
            ),
        [metricConfigs, isTreeView, expandedRowKeys, onToggleExpand, onMetricChange, showEditableIndicators],
    );

    const dataSource = useMemo(
        () =>
            isTreeView
                ? buildTreeData(ppgData, metricConfigs, baseline)
                : buildFlatData(ppgData, metricConfigs, baseline),
        [isTreeView, metricConfigs, ppgData, baseline],
    );

    return (
        <Table<TableRow>
            columns={columns}
            dataSource={dataSource}
            pagination={false}
            size="small"
            scroll={{ x: 'max-content', y: 500 }}
            expandable={{
                expandedRowKeys,
                onExpandedRowsChange: (keys) => setExpandedRowKeys(keys as string[]),
                expandIcon: () => null,
                indentSize: 0,
            }}
        />
    );
};

PromoMetricsTable.displayName = 'PromoMetricsTable';

export default PromoMetricsTable;
